import { Button, ErrorMessage, Skeleton, Tooltip } from '@hive/component';
import { SubscriptionPlan } from '@hive/graphql';
import { useI18n } from '@hive/i18n';
import {
  useLiveData,
  useService,
  useServiceOptional,
} from '@toeverything/infra';
import { cssVar } from '@toeverything/theme';
import { useEffect, useMemo } from 'react';

import {
  ServerService,
  SubscriptionService,
  UserQuotaService,
  WorkspaceSubscriptionService,
} from '../../../../modules/cloud';
import { WorkspaceQuotaService } from '../../../../modules/quota';
import * as styles from './storage-progress.css';

export interface StorageProgressProgress {
  upgradable?: boolean;
  onUpgrade: () => void;
}

enum ButtonType {
  Primary = 'primary',
  Default = 'secondary',
}

export const StorageProgress = ({ onUpgrade }: StorageProgressProgress) => {
  const t = useI18n();
  const userQuota = useService(UserQuotaService).quota;

  // Check workspace subscription (for team plans)
  const workspaceSubscriptionService = useServiceOptional(
    WorkspaceSubscriptionService
  );
  const workspaceSubscription = useLiveData(
    workspaceSubscriptionService?.subscription.subscription$
  );
  const isTeamWorkspace = workspaceSubscription?.plan === SubscriptionPlan.Team;

  // Get workspace quota service for team workspaces
  const workspaceQuotaService = useServiceOptional(WorkspaceQuotaService);
  const workspaceQuota = workspaceQuotaService?.quota;

  // Use workspace quota for team workspaces, user quota otherwise
  const activeQuota =
    isTeamWorkspace && workspaceQuota ? workspaceQuota : userQuota;

  useEffect(() => {
    // revalidate quota to get the latest status
    activeQuota.revalidate();
  }, [activeQuota]);

  const color = useLiveData(activeQuota.color$);
  const usedFormatted = useLiveData(activeQuota.usedFormatted$);
  const maxFormatted = useLiveData(activeQuota.maxFormatted$);
  const percent = useLiveData(activeQuota.percent$);

  const serverService = useService(ServerService);
  const hasPaymentFeature = useLiveData(
    serverService.server.features$.map(f => f?.payment)
  );
  const subscription = useService(SubscriptionService).subscription;
  useEffect(() => {
    // revalidate subscription to get the latest status
    subscription.revalidate();
  }, [subscription]);

  useEffect(() => {
    workspaceSubscriptionService?.subscription.revalidate();
  }, [workspaceSubscriptionService]);

  const proSubscription = useLiveData(subscription.pro$);

  // Check if user has a lifetime plan from quota
  const hasPaidPlan = useLiveData(userQuota.hasPaidPlan$);

  // User is not "free" if they have a personal subscription OR are in a team workspace OR have a lifetime plan
  const isFreeUser = !proSubscription && !isTeamWorkspace && !hasPaidPlan;

  // Get quota name from the appropriate source
  const userQuotaName = useLiveData(
    userQuota.quota$.map(q => (q !== null ? q?.humanReadable.name : null))
  );
  const workspaceQuotaName = useLiveData(
    workspaceQuota?.quota$.map(q => (q !== null ? q?.humanReadable.name : null))
  );

  // Display team workspace quota name if in team workspace
  const displayQuotaName = isTeamWorkspace
    ? (workspaceQuotaName ??
      t['com.hive.payment.cloud.team-workspace.name']?.() ??
      'Team Plan')
    : userQuotaName;

  const loading =
    proSubscription === null || percent === null || displayQuotaName === null;
  const loadError = useLiveData(activeQuota.error$);

  const buttonType = useMemo(() => {
    if (isFreeUser) {
      return ButtonType.Primary;
    }
    return ButtonType.Default;
  }, [isFreeUser]);

  if (loading) {
    if (loadError) {
      // TODO(@catsjuice): i18n
      return <ErrorMessage>Load error</ErrorMessage>;
    }
    return <Skeleton height={42} />;
  }

  return (
    <div className={styles.storageProgressContainer}>
      <div className={styles.storageProgressWrapper}>
        <div className="storage-progress-desc">
          <span>{t['com.hive.storage.used.hint']()}</span>
          <span>
            {usedFormatted}/{maxFormatted}
            {` (${displayQuotaName} ${t['com.hive.storage.plan']()})`}
          </span>
        </div>

        <div className="storage-progress-bar-wrapper">
          <div
            className={styles.storageProgressBar}
            style={{
              width: `${percent}%`,
              backgroundColor: color ?? cssVar('processingColor'),
            }}
          ></div>
        </div>
      </div>

      {hasPaymentFeature ? (
        <Tooltip
          options={{ hidden: percent < 100 }}
          content={
            isFreeUser
              ? t['com.hive.storage.maximum-tips']()
              : t['com.hive.storage.maximum-tips.pro']()
          }
        >
          <span tabIndex={0}>
            <Button variant={buttonType} onClick={onUpgrade}>
              {isFreeUser
                ? t['com.hive.storage.upgrade']()
                : t['com.hive.storage.change-plan']()}
            </Button>
          </span>
        </Tooltip>
      ) : null}
    </div>
  );
};
